// Formatting helpers for the Session Payouts admin screen.
// All money from the API is in paise (integers); the UI shows rupees with two decimals.

export const STATUS_LABELS = {
  on_hold:   'On hold',
  released:  'Ready to pay',
  paid:      'Paid',
  cancelled: 'Cancelled',
};

export const STATUS_COLORS = {
  on_hold:   { bg: '#fef3c7', fg: '#92400e' },
  released:  { bg: '#d1fae5', fg: '#065f46' },
  paid:      { bg: '#dbeafe', fg: '#1e40af' },
  cancelled: { bg: '#f3f4f6', fg: '#4b5563' },
};

export const PAYMENT_MODE_LABELS = {
  bank_transfer: 'Bank transfer',
  upi:           'UPI',
  cash:          'Cash',
  cheque:        'Cheque',
  other:         'Other',
};

export function fmtRupees(paise) {
  const n = Number(paise);
  const rupees = Number.isFinite(n) ? n / 100 : 0;
  return '₹' + rupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}

// Last 6 characters of a Mongo id — enough to recognise a request without showing the whole id.
export function shortId(id) {
  return id ? String(id).slice(-6) : '—';
}

// Hold flags shown next to the status of an on-hold line.
export function holdBadges(line) {
  const badges = [];
  if (line && line.patientHeldAt) badges.push('Patient hold');
  if (line && line.adminHoldNote) badges.push('Admin hold');
  return badges;
}

// Add up the per-healer summary rows into the three strip totals.
export function sumSummary(rows) {
  const t = {
    onHold: { amount: 0, count: 0 },
    ready:  { amount: 0, count: 0 },
    paid:   { amount: 0, count: 0 },
  };
  (rows || []).forEach(function(r) {
    t.onHold.amount += r.onHoldAmount || 0;
    t.onHold.count  += r.onHoldCount  || 0;
    t.ready.amount  += r.releasedUnpaidAmount || 0;
    t.ready.count   += r.releasedUnpaidCount  || 0;
    t.paid.amount   += r.paidAmount || 0;
    t.paid.count    += r.paidCount  || 0;
  });
  return t;
}

// <input type="date"> gives YYYY-MM-DD in local time; the API filters on createdAt as an ISO instant.
export function startOfDayISO(dateStr) {
  return new Date(dateStr + 'T00:00:00').toISOString();
}

export function endOfDayISO(dateStr) {
  return new Date(dateStr + 'T23:59:59.999').toISOString();
}

export function apiErrorMessage(err, fallback) {
  if (err && err.response) {
    return (err.response.data && err.response.data.message) || fallback;
  }
  return 'No connection to the server. Check your internet and try again.';
}

// Which row actions the backend allows for each status (mark paid is a separate batch action on released lines).
export const ACTIONS_BY_STATUS = {
  on_hold:   ['hold', 'release', 'cancel'],
  released:  ['cancel'],
  paid:      [],
  cancelled: [],
};

export const ACTION_LABELS = { hold: 'Hold', release: 'Release', cancel: 'Cancel' };

// Notes, reasons and references must be 3 to max characters after trimming (same limits as the API).
export function textValid(value, min, max) {
  const n = String(value || '').trim().length;
  return n >= min && n <= max;
}

// Today as YYYY-MM-DD in local time, for <input type="date">.
export function todayStr() {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

// The line changed under the admin (someone else acted, or it was already paid/cancelled).
const STALE_CODES = ['LINE_NOT_ON_HOLD', 'NOT_CANCELLABLE', 'NO_ELIGIBLE_LINES'];
export function isStaleCode(code) {
  return STALE_CODES.indexOf(code) !== -1;
}

const CODE_MESSAGES = {
  LINE_NOT_ON_HOLD:  'This line has changed since you opened the page. The list was refreshed.',
  NOT_CANCELLABLE:   "Paid or already cancelled lines can't be cancelled. The list was refreshed.",
  NO_ELIGIBLE_LINES: 'None of the selected lines can be paid now. The list was refreshed.',
};

// Hold days for the payout settings: a whole number from 0 to 30 (same limits as the API).
export function holdDaysValid(value) {
  const s = String(value).trim();
  return /^\d+$/.test(s) && Number(s) >= 0 && Number(s) <= 30;
}

// The schedule a settings save sends back unchanged. A manual schedule has no day: a stale stored day would be refused by the API.
export function scheduleToKeep(cfg) {
  if (!cfg) return {};
  return { frequency: cfg.frequency, payoutDay: cfg.frequency === 'manual' ? null : cfg.payoutDay };
}

// Plain-language text for errors from PUT /api/settings/payout-config (codes and fields as the API sends them).
export function settingsErrorMessage(err) {
  const d = err && err.response && err.response.data;
  if (d && d.code === 'SOLO_TENANT') return "Per-session payouts aren't available for solo practices.";
  if (d && d.code === 'SESSION_PAYOUT_ENABLED') return 'Turn off per-session payouts before changing the payout schedule.';
  if (d && d.field === 'sessionPayoutHoldDays') return 'Hold days must be a whole number from 0 to 30.';
  if (d && d.field === 'sessionPayoutEnabled') return 'Per-session payouts can only be turned on when the payout schedule is manual.';
  if (err && err.response && err.response.status === 403) return "You don't have access to change payout settings.";
  if (err && err.response && err.response.status === 404) return 'Centre not found. Please sign in again.';
  return apiErrorMessage(err, 'Could not save the payout settings. Please try again.');
}

export function actionErrorMessage(err, fallback) {
  const code = err && err.response && err.response.data && err.response.data.code;
  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];
  if (err && err.response && err.response.status === 403) return "You don't have access to do this.";
  return apiErrorMessage(err, fallback);
}
